import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Armchair,
  ArrowLeft,
  ArrowRight,
  Bell,
  Check,
  LoaderCircle,
  LogIn,
  LogOut,
  MapPin,
  RotateCw,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";

import type { FloorDesk, FloorDesks, Workday } from "../api/types";
import {
  useAtDeskSeconds,
  useCheckIn,
  useCheckOut,
  useClaimDesk,
  useFloorDesks,
  useJoinDeskWaitlist,
} from "../hooks/useWorkday";
import { formatHoursMinutes } from "../lib/format";
import { markOfficeLeft } from "../lib/officeSession";
import { clockTime, firstName, greetingFor, initialsOf, longDate } from "../lib/workday";
import { HwAvatar } from "./hw";

/**
 * The HomeWork check-in gate, the end-of-day screen, the check-out dialog and
 * the first-time "claim a desk" flow — weco2/homework-desk.jsx, on the office
 * service's real `/me/workday/` and `/me/desks/` instead of the mock's
 * localStorage store and fixture desks.
 */

/** What the desk card and the header subtitle show about a desk. */
export interface DeskSummary {
  number: number;
  label: string;
  pod: string;
  area: string;
  building: string;
}

export function deskSummaryOf(workday: Workday | null | undefined): DeskSummary | null {
  const desk = workday?.desk;
  if (!desk) return null;
  return {
    number: desk.number,
    label: desk.label,
    pod: desk.pod,
    area: desk.zone_name,
    building: desk.floor_name,
  };
}

function deskSummaryFrom(desk: FloorDesk, floorName: string): DeskSummary {
  return { number: desk.number, label: desk.label, pod: desk.pod, area: desk.zone_name, building: floorName };
}

export function DeskInfo({ desk, note }: { desk: DeskSummary | null; note?: string | null }) {
  const { t } = useTranslation();
  if (!desk) {
    return (
      <div className="hwd-info">
        <span className="hwd-tile is-empty">
          <Armchair size={20} />
        </span>
        <span className="min-w-0">
          <span className="hw-ct block" style={{ fontSize: 15 }}>
            {t("homework.workday.noDesk", { defaultValue: "No desk claimed" })}
          </span>
          <span className="hw-muted block" style={{ fontSize: 13 }}>
            {t("homework.workday.noDeskLine", {
              defaultValue: "You'll check in without a seat on the office map.",
            })}
          </span>
        </span>
      </div>
    );
  }
  return (
    <div className="hwd-info">
      <span className="hwd-tile">
        <Armchair size={20} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="hw-ct block" style={{ fontSize: 15 }}>
          {t("homework.workday.deskPod", {
            desk: desk.label,
            pod: desk.pod,
            defaultValue: "{{desk}} · Pod {{pod}}",
          })}
        </span>
        <span className="hw-muted flex items-center gap-1" style={{ fontSize: 13 }}>
          <MapPin size={13} />
          {desk.area} · {desk.building}
        </span>
      </span>
      {note && (
        <span className="hw-pill shrink-0" style={{ background: "var(--btn-toggled-bg)", color: "var(--btn-soft-text)" }}>
          {note}
        </span>
      )}
    </div>
  );
}

function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function Alert({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="hwd-alert" role="alert">
      <TriangleAlert size={18} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>
        {title && <strong style={{ display: "block", marginBottom: 2 }}>{title}</strong>}
        {children}
      </span>
    </div>
  );
}

/** The shared check-in panel body: date, greeting, live clock, desk, button. */
function CheckInBody({
  name,
  desk,
  first = false,
  note,
  finishOnboarding = false,
  onDone,
}: {
  name: string;
  desk: DeskSummary | null;
  first?: boolean;
  note?: string | null;
  finishOnboarding?: boolean;
  onDone?: () => void;
}) {
  const { t } = useTranslation();
  const now = useClock();
  const checkIn = useCheckIn();
  const busy = checkIn.isPending;
  const failed = checkIn.isError;
  const run = () =>
    checkIn.mutate({ finishOnboarding }, { onSuccess: () => onDone?.() });
  const greeting = t(`homework.workday.greeting.${greetingFor(now)}`, {
    name: firstName(name),
    defaultValue: "Hello, {{name}}",
  });

  return (
    <>
      <div className="flex flex-col gap-2">
        <p className="hw-over">{longDate(now)}</p>
        <h1 className="hwd-h">
          {first
            ? t("homework.workday.firstTitle", { defaultValue: "Check in for the first time" })
            : greeting}
        </h1>
        <p className="hwd-sub">
          {first
            ? t("homework.workday.firstSub", {
                defaultValue: "Checking in puts you at your desk and opens your day.",
              })
            : t("homework.workday.sub", { defaultValue: "Check in to sit at your desk and open Homework." })}
        </p>
      </div>
      <div
        className="hwd-clock"
        aria-label={t("homework.workday.clockAria", {
          time: clockTime(now),
          defaultValue: "Current time {{time}}",
        })}
      >
        {clockTime(now)}
      </div>
      <DeskInfo desk={desk} note={note} />
      {failed && (
        <Alert title={t("homework.workday.failedTitle", { defaultValue: "Check-in didn't go through" })}>
          {t("homework.workday.failedLine", {
            defaultValue:
              "We couldn't reach the office. Your time isn't being counted yet, so try again in a moment.",
          })}
        </Alert>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="hw-btn hw-pri hwd-big" onClick={run} disabled={busy} aria-busy={busy}>
          {busy ? (
            <>
              <LoaderCircle size={18} className="animate-spin" />
              {t("homework.workday.checkingIn", { defaultValue: "Checking in…" })}
            </>
          ) : failed ? (
            <>
              <RotateCw size={18} />
              {t("homework.workday.tryAgain", { defaultValue: "Try again" })}
            </>
          ) : (
            <>
              <LogIn size={18} className="rtl:-scale-x-100" />
              {t("homework.workday.checkIn", { defaultValue: "Check in" })}
            </>
          )}
        </button>
        {!first && (
          <span className="hw-muted" style={{ fontSize: 13 }}>
            {t("homework.workday.hoursStart", { defaultValue: "Your hours start counting when you check in." })}
          </span>
        )}
      </div>
    </>
  );
}

export function CheckInScreen({ workday, name, onDone }: { workday: Workday; name: string; onDone?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="hwd-stage">
      <section className="hwd-panel hw-fade" aria-label={t("homework.workday.checkIn", { defaultValue: "Check in" })}>
        <CheckInBody name={name} desk={deskSummaryOf(workday)} onDone={onDone} />
      </section>
    </div>
  );
}

export function CheckedOutScreen({ workday, name, onDone }: { workday: Workday; name: string; onDone?: () => void }) {
  const { t } = useTranslation();
  const checkIn = useCheckIn();
  const seconds = useAtDeskSeconds(workday);
  const rows: [string, string][] = [
    [t("homework.workday.checkedIn", { defaultValue: "Checked in" }), clockTime(workday.first_checked_in_at)],
    [t("homework.workday.checkedOut", { defaultValue: "Checked out" }), clockTime(workday.checked_out_at)],
    [t("homework.workday.atDesk", { defaultValue: "At your desk" }), formatHoursMinutes(seconds)],
  ];
  return (
    <div className="hwd-stage">
      <section className="hwd-panel hw-fade" aria-label={t("homework.workday.checkedOut", { defaultValue: "Checked out" })}>
        <div className="flex flex-col gap-2">
          <p className="hw-over">{t("homework.workday.endOfDay", { defaultValue: "End of day" })}</p>
          <h1 className="hwd-h">
            {t("homework.workday.seeYou", { name: firstName(name), defaultValue: "See you tomorrow, {{name}}" })}
          </h1>
          <p className="hwd-sub">
            {t("homework.workday.outLine", {
              defaultValue: "You've checked out and left your desk. Teammates see you as offline.",
            })}
          </p>
        </div>
        <dl className="m-0 grid gap-3" style={{ gridTemplateColumns: "repeat(3,minmax(0,1fr))" }}>
          {rows.map(([label, value]) => (
            <div key={label} className="hwd-info" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
              <dt className="hw-muted" style={{ fontSize: 12 }}>
                {label}
              </dt>
              <dd className="hw-num hw-strongtext m-0" style={{ fontSize: 20, fontWeight: 700 }}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
        <DeskInfo desk={deskSummaryOf(workday)} />
        {checkIn.isError && (
          <Alert>
            {t("homework.workday.againFailed", {
              defaultValue: "We couldn't check you back in. Try again in a moment.",
            })}
          </Alert>
        )}
        <div>
          <button
            type="button"
            className="hw-btn hw-sec"
            onClick={() => checkIn.mutate({}, { onSuccess: () => onDone?.() })}
            disabled={checkIn.isPending}
          >
            {checkIn.isPending ? <LoaderCircle size={16} className="animate-spin" /> : <LogIn size={16} className="rtl:-scale-x-100" />}
            {checkIn.isPending
              ? t("homework.workday.checkingIn", { defaultValue: "Checking in…" })
              : t("homework.workday.checkInAgain", { defaultValue: "Check in again" })}
          </button>
        </div>
      </section>
    </div>
  );
}

/**
 * "Check out for today?" — opened from the suite header's Check out button.
 * Rendered fixed over the whole page, so it works from any module.
 */
export function CheckOutDialog({
  workday,
  onClose,
  onCheckedOut,
}: {
  workday: Workday;
  onClose: () => void;
  onCheckedOut?: () => void;
}) {
  const { t } = useTranslation();
  const checkOut = useCheckOut();
  const seconds = useAtDeskSeconds(workday);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="hwd-dialog-layer">
      <div className="hw-scrim" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="hwd-co-t"
        className="hwd-panel hw-fade"
        style={{
          position: "fixed",
          zIndex: 41,
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          maxWidth: 420,
          width: "calc(100% - 32px)",
          padding: 24,
          gap: 14,
        }}
      >
        <h2 id="hwd-co-t" className="hw-ct" style={{ fontSize: 18 }}>
          {t("homework.workday.outTitle", { defaultValue: "Check out for today?" })}
        </h2>
        <p className="hw-muted m-0">
          {t("homework.workday.outBody", {
            duration: formatHoursMinutes(seconds),
            time: clockTime(workday.first_checked_in_at),
            defaultValue:
              "You've been at your desk {{duration}} since {{time}}. You'll need to check in again to open Homework.",
          })}
        </p>
        {checkOut.isError && (
          <Alert>
            {t("homework.workday.outFailed", { defaultValue: "We couldn't check you out. Try again in a moment." })}
          </Alert>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="hw-btn hw-sec" onClick={onClose}>
            {t("homework.workday.stay", { defaultValue: "Stay checked in" })}
          </button>
          <button
            type="button"
            className="hw-btn hw-pri"
            autoFocus
            disabled={checkOut.isPending}
            onClick={() =>
              checkOut.mutate(undefined, {
                onSuccess: () => {
                  // Off the floor in this tab straight away, wherever in the
                  // suite the person checked out from.
                  markOfficeLeft();
                  onCheckedOut?.();
                  onClose();
                },
              })
            }
          >
            {checkOut.isPending ? <LoaderCircle size={16} className="animate-spin" /> : <LogOut size={16} className="rtl:-scale-x-100" />}
            {t("homework.workday.checkOut", { defaultValue: "Check out" })}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------- first-time flow

/** Desks grouped by pod, in the order the server sent them. */
function podsOf(desks: FloorDesk[]): [string, FloorDesk[]][] {
  const pods = new Map<string, FloorDesk[]>();
  for (const desk of desks) {
    const list = pods.get(desk.pod);
    if (list) list.push(desk);
    else pods.set(desk.pod, [desk]);
  }
  return [...pods.entries()];
}

export function DeskPicker({
  data,
  value,
  onPick,
}: {
  data: FloorDesks;
  value: string | null;
  onPick: (deskId: string) => void;
}) {
  const { t } = useTranslation();
  const zones = new Set(data.desks.map((d) => d.zone_name));
  const place = zones.size === 1 ? `${[...zones][0]} · ${data.floor.name}` : data.floor.name;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="hw-ct" style={{ fontSize: 14 }}>
          {place}
        </span>
        <div className="hwd-legend">
          <span>
            <i />
            {t("homework.onboard.available", { defaultValue: "Available" })}
          </span>
          <span>
            <i className="t" />
            {t("homework.onboard.taken", { defaultValue: "Taken" })}
          </span>
          <span>
            <i className="s" />
            {t("homework.onboard.yourPick", { defaultValue: "Your pick" })}
          </span>
        </div>
      </div>
      <div
        className="hwd-plan"
        role="group"
        aria-label={t("homework.onboard.planAria", { defaultValue: "Desks on the floor" })}
      >
        {podsOf(data.desks).map(([pod, desks]) => (
          <div key={pod} className="hwd-pod">
            <span className="hwd-pod-l">{t("homework.onboard.pod", { pod, defaultValue: "Pod {{pod}}" })}</span>
            <div className="hwd-pod-g">
              {desks.map((desk) => {
                const owner = desk.mine ? null : desk.owner;
                const taken = Boolean(owner);
                return (
                  <button
                    key={desk.id}
                    type="button"
                    className="hwd-desk"
                    disabled={taken}
                    aria-pressed={value === desk.id}
                    onClick={() => onPick(desk.id)}
                    aria-label={
                      taken
                        ? t("homework.onboard.deskTakenBy", {
                            desk: desk.number,
                            name: owner!.name,
                            defaultValue: "Desk {{desk}}, taken by {{name}}",
                          })
                        : t("homework.onboard.deskFree", {
                            desk: desk.number,
                            defaultValue: "Desk {{desk}}, available",
                          })
                    }
                    title={
                      taken
                        ? t("homework.onboard.takenBy", { name: owner!.name, defaultValue: "Taken by {{name}}" })
                        : t("homework.onboard.available", { defaultValue: "Available" })
                    }
                  >
                    {desk.number}
                    <small>
                      {taken
                        ? initialsOf(owner!.name)
                        : value === desk.id
                          ? t("homework.onboard.yours", { defaultValue: "Yours" })
                          : t("homework.onboard.free", { defaultValue: "Free" })}
                    </small>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

type Step = "welcome" | "claim" | "confirm" | "checkin";

export function Onboarding({ workday, name, onFinish }: { workday: Workday; name: string; onFinish?: () => void }) {
  const { t } = useTranslation();
  const { data: floorDesks } = useFloorDesks();
  const claim = useClaimDesk();
  const waitlist = useJoinDeskWaitlist();
  // An admin seated this person before they ever opened HomeWork: there is
  // nothing to pick, only a desk to confirm.
  const preAssigned = workday.desk?.assigned_by ? workday.desk : null;
  const steps: Step[] = preAssigned ? ["welcome", "confirm", "checkin"] : ["welcome", "claim", "confirm", "checkin"];
  const [index, setIndex] = useState(0);
  const [pick, setPick] = useState<string | null>(null);
  const [noSeat, setNoSeat] = useState(false);
  const step = steps[index] ?? "welcome";
  const next = () => setIndex((i) => Math.min(steps.length - 1, i + 1));
  const back = () => setIndex((i) => Math.max(0, i - 1));

  const desks = floorDesks?.desks ?? [];
  const mine = desks.find((d) => d.mine) ?? null;
  // Somebody who already holds a desk (claimed it on the floor before this
  // flow existed) starts with it picked.
  useEffect(() => {
    if (!pick && mine) setPick(mine.id);
  }, [mine, pick]);
  const picked = desks.find((d) => d.id === pick) ?? null;
  const noDesks = Boolean(floorDesks) && floorDesks!.free === 0 && !mine;

  const chosen: DeskSummary | null = noSeat
    ? null
    : preAssigned
      ? deskSummaryOf(workday)
      : picked && floorDesks
        ? deskSummaryFrom(picked, floorDesks.floor.name)
        : null;
  const pod = preAssigned?.pod ?? picked?.pod;
  const chosenId = preAssigned?.id ?? picked?.id;
  const neighbours = desks.filter((d) => d.pod === pod && d.id !== chosenId && d.owner && !d.mine);

  const claimAndContinue = () => {
    if (!picked) return;
    if (picked.mine) {
      next();
      return;
    }
    claim.mutate(picked.id, {
      onSuccess: next,
      onError: (error: unknown) => {
        setPick(null);
        toast.error(
          error instanceof Error
            ? error.message
            : t("homework.onboard.claimFailed", { defaultValue: "That desk was just taken. Pick another." }),
        );
      },
    });
  };

  let body: ReactNode;
  if (step === "welcome") {
    body = (
      <>
        <div className="hwd-mini" aria-hidden>
          {Array.from({ length: 18 }).map((_, k) => (
            <span key={k} className={k === 8 ? "me" : [1, 3, 7, 10, 14].includes(k) ? "p" : ""} />
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <p className="hw-over">{t("homework.onboard.welcome", { defaultValue: "Welcome to Homework" })}</p>
          <h1 className="hwd-h">{t("homework.onboard.welcomeTitle", { defaultValue: "Your desk for the workday" })}</h1>
          <p className="hwd-sub">
            {t("homework.onboard.welcomeSub", {
              defaultValue: "Sit at a virtual desk while you work, so your team can see you're in and find you.",
            })}
          </p>
        </div>
        <div>
          <button type="button" className="hw-btn hw-pri hwd-big" onClick={next}>
            {preAssigned
              ? t("homework.onboard.seeMyDesk", { defaultValue: "See my desk" })
              : t("homework.onboard.claimADesk", { defaultValue: "Claim a desk" })}
            <ArrowRight size={18} className="rtl:-scale-x-100" />
          </button>
        </div>
      </>
    );
  } else if (step === "claim") {
    body = !floorDesks ? (
      <>
        <h1 className="hwd-h">{t("homework.onboard.claimTitle", { defaultValue: "Claim your desk" })}</h1>
        <div className="hw-sk" style={{ height: 240, borderRadius: 24 }} aria-busy="true" />
      </>
    ) : noDesks ? (
      <>
        <div className="flex flex-col gap-2">
          <h1 className="hwd-h">{t("homework.onboard.claimTitle", { defaultValue: "Claim your desk" })}</h1>
        </div>
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <span className="hwd-tile" style={{ width: 56, height: 56, borderRadius: 20 }}>
            <Armchair size={24} />
          </span>
          <p className="hw-ct">{t("homework.onboard.noneFree", { defaultValue: "No desks are free right now" })}</p>
          <p className="hw-muted m-0" style={{ maxWidth: "44ch" }}>
            {t("homework.onboard.noneFreeLine", {
              total: floorDesks.total,
              floor: floorDesks.floor.name,
              defaultValue:
                "All {{total}} desks on {{floor}} are claimed. Ask your admin for one, or continue and claim a desk later.",
            })}
          </p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              className="hw-btn hw-sec"
              disabled={waitlist.isPending || workday.waitlisted}
              onClick={() =>
                waitlist.mutate(undefined, {
                  onSuccess: () =>
                    toast(t("homework.onboard.notifyDone", { defaultValue: "We'll let you know when a desk frees up" })),
                })
              }
            >
              <Bell size={16} />
              {t("homework.onboard.notifyMe", { defaultValue: "Notify me" })}
            </button>
            <button
              type="button"
              className="hw-btn hw-pri"
              onClick={() => {
                setPick(null);
                setNoSeat(true);
                setIndex(steps.indexOf("checkin"));
              }}
            >
              {t("homework.onboard.withoutDesk", { defaultValue: "Continue without a desk" })}
              <ArrowRight size={16} className="rtl:-scale-x-100" />
            </button>
          </div>
        </div>
      </>
    ) : (
      <>
        <div className="flex flex-col gap-2">
          <h1 className="hwd-h">{t("homework.onboard.claimTitle", { defaultValue: "Claim your desk" })}</h1>
          <p className="hwd-sub">
            {t("homework.onboard.claimSub", { defaultValue: "Pick a free desk. It stays yours until you release it." })}
          </p>
        </div>
        <DeskPicker data={floorDesks} value={pick} onPick={setPick} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="hw-muted" style={{ fontSize: 13 }}>
            {picked
              ? t("homework.workday.deskPod", { desk: picked.label, pod: picked.pod, defaultValue: "{{desk}} · Pod {{pod}}" })
              : t("homework.onboard.freeOf", {
                  free: floorDesks.free,
                  total: floorDesks.total,
                  defaultValue: "{{free}} of {{total}} desks free",
                })}
          </span>
          <button type="button" className="hw-btn hw-pri" disabled={!picked || claim.isPending} onClick={claimAndContinue}>
            {claim.isPending && <LoaderCircle size={16} className="animate-spin" />}
            {picked
              ? t("homework.onboard.claimDesk", { desk: picked.number, defaultValue: "Claim desk {{desk}}" })
              : t("homework.onboard.pickADesk", { defaultValue: "Pick a desk" })}
            <ArrowRight size={16} className="rtl:-scale-x-100" />
          </button>
        </div>
      </>
    );
  } else if (step === "confirm") {
    body = (
      <>
        <div className="flex flex-col gap-2">
          <p className="hw-over">
            {preAssigned
              ? t("homework.onboard.assignedToYou", { defaultValue: "Assigned to you" })
              : t("homework.onboard.claimed", { defaultValue: "Desk claimed" })}
          </p>
          <h1 className="hwd-h">{t("homework.onboard.thisIsYourDesk", { defaultValue: "This is your desk" })}</h1>
          <p className="hwd-sub">
            {preAssigned
              ? t("homework.onboard.setUpBy", {
                  name: preAssigned.assigned_by?.name ?? "",
                  defaultValue: "{{name}} set this desk up for you. Confirm it to continue.",
                })
              : t("homework.onboard.changeLater", { defaultValue: "You can change desks later from the office map." })}
          </p>
        </div>
        {chosen && (
          <div className="hwd-confirm">
            <span className="hwd-confirm-tile">
              <Armchair size={22} />
              <span className="hw-num" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>
                {chosen.number}
              </span>
            </span>
            <dl
              className="m-0 grid min-w-0 gap-x-4 gap-y-1"
              style={{ gridTemplateColumns: "auto minmax(0,1fr)", fontSize: 14 }}
            >
              <dt className="hw-muted">{t("homework.onboard.desk", { defaultValue: "Desk" })}</dt>
              <dd className="hw-strongtext m-0 font-semibold">
                {t("homework.workday.deskPod", { desk: chosen.label, pod: chosen.pod, defaultValue: "{{desk}} · Pod {{pod}}" })}
              </dd>
              <dt className="hw-muted">{t("homework.onboard.location", { defaultValue: "Location" })}</dt>
              <dd className="m-0">
                {chosen.area}, {chosen.building}
              </dd>
              <dt className="hw-muted">{t("homework.onboard.neighbours", { defaultValue: "Neighbours" })}</dt>
              <dd className="m-0 flex min-w-0 items-center gap-1.5">
                {neighbours.length ? (
                  <>
                    <span className="flex -space-x-1.5 rtl:space-x-reverse">
                      {neighbours.map((d) => (
                        <HwAvatar key={d.id} name={d.owner!.name} id={d.owner!.id} url={d.owner!.avatar_url} size={22} ring={false} />
                      ))}
                    </span>
                    <span className="truncate">{neighbours.map((d) => firstName(d.owner!.name)).join(", ")}</span>
                  </>
                ) : (
                  t("homework.onboard.nobodyYet", { defaultValue: "Nobody yet" })
                )}
              </dd>
            </dl>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button type="button" className="hw-btn hw-pri hwd-big" onClick={next}>
            {t("homework.onboard.confirm", { defaultValue: "Confirm desk" })}
            <Check size={18} />
          </button>
        </div>
      </>
    );
  } else {
    body = (
      <CheckInBody
        name={name}
        desk={chosen}
        first
        finishOnboarding
        note={preAssigned ? t("homework.onboard.assigned", { defaultValue: "Assigned" }) : null}
        onDone={onFinish}
      />
    );
  }

  return (
    <div className="hwd-stage">
      <section
        className={`hwd-panel hw-fade ${step === "claim" ? "wide" : ""}`}
        key={step}
        aria-label={t("homework.onboard.aria", { defaultValue: "Set up your desk" })}
      >
        <div className="hwd-prog">
          <button
            type="button"
            className="hw-btn hw-sec hw-ico"
            style={{ width: 36, height: 36, minHeight: 36, visibility: index ? "visible" : "hidden" }}
            onClick={() => {
              if (step === "checkin" && noSeat) {
                setNoSeat(false);
                setIndex(steps.indexOf("claim"));
              } else back();
            }}
            aria-label={t("homework.onboard.back", { defaultValue: "Back" })}
          >
            <ArrowLeft size={16} className="rtl:-scale-x-100" />
          </button>
          <div
            className="hwd-steps"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={steps.length}
            aria-valuenow={index + 1}
            aria-label={t("homework.onboard.stepOf", {
              step: index + 1,
              total: steps.length,
              defaultValue: "Step {{step}} of {{total}}",
            })}
          >
            {steps.map((s, k) => (
              <span key={s} className={k <= index ? "on" : ""} />
            ))}
          </div>
          <span className="hw-muted hw-num shrink-0" style={{ fontSize: 12, fontWeight: 600 }}>
            {t("homework.onboard.stepOf", { step: index + 1, total: steps.length, defaultValue: "Step {{step}} of {{total}}" })}
          </span>
        </div>
        {body}
      </section>
    </div>
  );
}

/** The header subtitle: "Desk 14 · Pod D · Cairo HQ · Floor 3". */
export function useDeskLine(workday: Workday | undefined): string | null {
  const { t } = useTranslation();
  return useMemo(() => {
    const desk = deskSummaryOf(workday);
    if (!desk) return null;
    return t("homework.shell.deskLine", {
      desk: desk.label,
      pod: desk.pod,
      floor: desk.building,
      defaultValue: "{{desk}} · Pod {{pod}} · {{floor}}",
    });
  }, [workday, t]);
}
